// Sound crew rules shared by the server (server/sound.mjs, api/admin/action.mjs) and the pages.
// A staff row (table staff, migration 0005) carries is_admin, is_sound, sound_lead and active.

/** Does this show need a sound engineer? The admin's switch wins over the band's answer. */
export const soundNeeded = (app) => (typeof app.sound_needed === 'boolean' ? app.sound_needed : app.needs_sound_engineer === 'yes');

/** Runs the crew: assigns engineers, adds and removes them. */
export const isCrewBoss = (staff) => Boolean(staff && staff.active !== false && (staff.is_admin || staff.sound_lead));

/** May open /sound: engineers, plus admins and the sound lead who look after them. */
export const canSeeSound = (staff) => Boolean(staff && staff.active !== false && (staff.is_sound || isCrewBoss(staff)));

// Which staff may run which action of api/admin/action. Anything not listed is admin only.
const RULES = {
    sound_list: canSeeSound,
    sound_claim: (s) => Boolean(s && s.active !== false && s.is_sound),
    sound_release: (s) => Boolean(s && s.active !== false && s.is_sound),
    sound_assign: isCrewBoss,
    sound_unassign: isCrewBoss,
    sound_set_needed: isCrewBoss,
    crew_list: isCrewBoss,
    crew_add: isCrewBoss,
    crew_update: isCrewBoss,
};

export const mayRun = (action, staff) => {
    if (!staff || staff.active === false) return false;
    const rule = RULES[action];
    return rule ? rule(staff) : Boolean(staff.is_admin);
};

/** Today's date in Reykjavík (UTC all year) as YYYY-MM-DD. */
export const todayIso = (now = new Date()) => now.toISOString().slice(0, 10);

/** A booked show that has not happened yet. */
export const isUpcoming = (app, today = todayIso()) => app.status === 'approved' && Boolean(app.confirmed_date) && app.confirmed_date >= today;

/**
 * One show as the sound page shows it. The band's contact details only go to the engineer on the
 * show and to the crew bosses; anyone else sees what the night needs, not who to call.
 * @param assignment row of sound_assignments or null
 * @param names email -> name of staff
 */
export const gigView = (app, assignment, me, names = {}) => {
    const mine = Boolean(assignment && assignment.engineer_email === me.email);
    const contact = mine || isCrewBoss(me);
    return {
        id: app.id,
        ref: app.ref,
        date: app.confirmed_date,
        time: app.start_time || app.suggested_start_time,
        band: app.band_name,
        genre: app.genre,
        line_up: app.line_up || '',
        tech_needs: app.tech_needs || '',
        audience: app.audience_estimate,
        event_slug: app.event_slug,
        needed: soundNeeded(app),
        engineer: assignment ? { email: assignment.engineer_email, name: names[assignment.engineer_email] || assignment.engineer_email } : null,
        mine,
        ...(contact ? { contact: { name: app.contact_name, email: app.contact_email, phone: app.contact_phone } } : {}),
    };
};

/**
 * The shows someone sees on /sound: upcoming bookings that need sound and are open or theirs.
 * Crew bosses also see shows other engineers have, and shows that have an engineer but no longer
 * need one (so they can take them off).
 */
export const gigsFor = (apps, assignments, me, names = {}, today = todayIso()) => {
    const byApp = Object.fromEntries(assignments.map((a) => [a.application_id, a]));
    return apps
        .filter((a) => isUpcoming(a, today))
        .map((a) => gigView(a, byApp[a.id] || null, me, names))
        .filter((g) => (isCrewBoss(me) ? g.needed || g.engineer : g.needed && (!g.engineer || g.mine)))
        .sort((x, y) => (x.date === y.date ? String(x.time).localeCompare(String(y.time)) : x.date.localeCompare(y.date)));
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const cleanEmail = (v) => String(v || '').trim().toLowerCase();
export const isEmail = (v) => EMAIL_RE.test(cleanEmail(v));
